import {
  ChartBarIcon,
  CaretDownIcon,
  CaretUpIcon,
  ClockIcon,
  FileTextIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import React, { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { CourseInsightsSentimentLabel } from "../../../../graphql/generated/graphql";
import { colors, radii, shadowStyles, spacing } from "../../../../styles/design-system";
import { clampPercentage, formatPercentage } from "../../../../utils/analyticsUtils";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import ModuleInsightsActivityItem from "./ModuleInsightsActivityItem";
import { type ModuleActivityViewModel, type ModuleCardInsight } from "./insightsTypes";

type ModuleInsightsCardProps = {
  moduleCard: ModuleCardInsight;
  activities: ModuleActivityViewModel[];
  moduleIndex: number;
  defaultExpanded?: boolean;
};

const getSentimentStyle = (sentiment: CourseInsightsSentimentLabel) => {
  if (sentiment === CourseInsightsSentimentLabel.Negative) {
    return {
      backgroundColor: colors.error[50],
      borderColor: colors.error[200],
      textColor: colors.error[700],
      label: "Negative",
    };
  }

  if (sentiment === CourseInsightsSentimentLabel.Positive) {
    return {
      backgroundColor: colors.success[50],
      borderColor: colors.success[200],
      textColor: colors.success[700],
      label: "Positive",
    };
  }

  return {
    backgroundColor: colors.grey[100],
    borderColor: colors.grey[200],
    textColor: colors.grey[600],
    label: "Neutral",
  };
};

const ModuleInsightsCard: React.FC<ModuleInsightsCardProps> = ({
  moduleCard,
  activities,
  moduleIndex,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sentimentStyle = getSentimentStyle(moduleCard.sentiment);
  const estimatedTimeSeconds = activities.reduce((sum, activity) => sum + (activity.averageTimeSeconds ?? 0), 0);
  const estimatedTimeText = estimatedTimeSeconds > 0 ? formatDurationFromSeconds(estimatedTimeSeconds) : "—";
  const scoreColor =
    typeof moduleCard.aggregateGradePct === "number" && moduleCard.aggregateGradePct < 70
      ? colors.error[700]
      : typeof moduleCard.aggregateGradePct === "number" && moduleCard.aggregateGradePct < 85
        ? colors.warning[700]
        : colors.success[700];

  return (
    <View style={[styles.card, shadowStyles.xs]}>
      <Pressable style={styles.header} onPress={() => setExpanded((prev) => !prev)}>
        <View style={styles.headerContent}>
          <CustomText
            text={`Module ${moduleIndex}`}
            size="s"
            weight="medium"
            useTranslationText={false}
            style={styles.mutedText}
          />
          <CustomText text={moduleCard.moduleTitle} size="xxl" weight="bold" useTranslationText={false} />
          {moduleCard.moduleDescription ? (
            <CustomText
              text={moduleCard.moduleDescription}
              size="s"
              useTranslationText={false}
              style={styles.mutedText}
            />
          ) : null}
          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <FileTextIcon size={16} color={colors.grey[500]} />
              <CustomText
                text={`${activities.length} activities`}
                size="s"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <ClockIcon size={16} color={colors.grey[500]} />
              <CustomText text={estimatedTimeText} size="s" useTranslationText={false} style={styles.mutedText} />
            </View>
            <View style={styles.metaItem}>
              <UsersThreeIcon size={16} color={colors.grey[500]} />
              <CustomText
                text={`${formatPercentage(moduleCard.completionPct)} completion`}
                size="s"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <ChartBarIcon size={16} color={colors.grey[500]} />
              <CustomText
                text={
                  typeof moduleCard.aggregateGradePct === "number"
                    ? `${formatPercentage(moduleCard.aggregateGradePct)} avg score`
                    : "— avg score"
                }
                size="s"
                useTranslationText={false}
                style={[styles.mutedText, { color: scoreColor }]}
              />
            </View>
            <View
              style={[
                styles.pill,
                { backgroundColor: sentimentStyle.backgroundColor, borderColor: sentimentStyle.borderColor },
              ]}>
              <CustomText
                text={sentimentStyle.label}
                size="xs"
                useTranslationText={false}
                style={{ color: sentimentStyle.textColor }}
              />
            </View>
          </View>
        </View>
        <View style={styles.headerRight}>
          {expanded ? (
            <CaretUpIcon size={16} color={colors.grey[500]} />
          ) : (
            <CaretDownIcon size={16} color={colors.grey[500]} />
          )}
        </View>
      </Pressable>

      <View style={styles.completionRow}>
        <CustomText text="Student Progress" size="xs" useTranslationText={false} style={styles.mutedText} />
        <View style={styles.track}>
          <View style={[styles.fill, { width: `${clampPercentage(moduleCard.completionPct)}%` }]} />
        </View>
        <CustomText
          text={formatPercentage(moduleCard.completionPct)}
          size="xs"
          weight="bold"
          useTranslationText={false}
          style={styles.mutedText}
        />
      </View>

      {expanded ? (
        <View style={styles.content}>
          <CustomText
            text="Activities in this Module"
            size="s"
            weight="bold"
            useTranslationText={false}
            style={styles.activitiesTitle}
          />
          {activities.length === 0 ? (
            <CustomText
              text="No activity analytics found for this module yet."
              size="s"
              useTranslationText={false}
              style={styles.mutedText}
            />
          ) : (
            activities.map((activity, index) => (
              <ModuleInsightsActivityItem
                key={activity.id}
                activity={activity}
                activityIndex={index + 1}
                defaultExpanded={index === 0}
              />
            ))
          )}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.xl,
    padding: spacing.md,
    gap: spacing.sm,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  headerContent: {
    flex: 1,
    gap: 4,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.lg,
    alignItems: "center",
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  completionRow: {
    gap: 4,
    marginTop: 2,
  },
  track: {
    width: "100%",
    height: 6,
    borderRadius: radii.full,
    backgroundColor: colors.grey[200],
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    borderRadius: radii.full,
    backgroundColor: colors.success[500],
  },
  pill: {
    borderRadius: radii.full,
    borderWidth: 1,
    paddingHorizontal: spacing.xs,
    paddingVertical: 4,
    alignSelf: "flex-start",
  },
  content: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    marginHorizontal: -spacing.md,
    marginBottom: -spacing.md,
    padding: spacing.md,
    backgroundColor: colors.grey[50],
    gap: spacing.sm,
  },
  activitiesTitle: {
    color: colors.grey[700],
  },
  mutedText: {
    color: colors.grey[500],
  },
});

export default ModuleInsightsCard;
